import {
	ChevronDownIcon,
	PopoverTrigger,
	StarIcon,
	StarOutlineIcon,
} from "@au-schein-de/au-ui-library";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { RatingBreakdownEntry } from "./rating-breakdown-popover.component";
import { RatingBreakdownDrawer } from "./rating-breakdown-popover.component";

type ProductDetailsCTAUserRatingProps = {
	rating: string | number;
	ratingCount: number;
	horizontal?: boolean;
	ratingBreakdown?: ReadonlyArray<RatingBreakdownEntry>;
	isBreakdownOpen: boolean;
	onBreakdownOpenChange: (open: boolean) => void;
	isDesktop: boolean;
};

const FILLED_STAR_CLASS = "w-3 h-3 text-gray-700";
const EMPTY_STAR_CLASS = "w-3 h-3 text-gray-300 stroke-gray-800 stroke-[3px]";

function parseRating(rating: string | number): number {
	const raw = Number(rating);
	const finite = Number.isFinite(raw) ? raw : 0;
	return Math.min(Math.max(finite, 0), 5);
}

function buildStars(ratingClamped: number): ReactNode[] {
	const fullStars = Math.floor(ratingClamped);
	const hasHalfStar = ratingClamped % 1 >= 0.5 && ratingClamped % 1 < 1;
	return Array.from({ length: 5 }, (_, index) => {
		const key = `rating-star-${index}`;
		if (index < fullStars) {
			return <StarIcon.Raw key={key} className={FILLED_STAR_CLASS} />;
		}
		if (index === fullStars && hasHalfStar) {
			return (
				<div key={key} className="relative w-3 h-3">
					<StarOutlineIcon.Raw className={EMPTY_STAR_CLASS} />
					<div
						className="absolute inset-0 overflow-hidden"
						style={{ width: "50%" }}
					>
						<StarIcon.Raw className={FILLED_STAR_CLASS} />
					</div>
				</div>
			);
		}
		return <StarOutlineIcon.Raw key={key} className={EMPTY_STAR_CLASS} />;
	});
}

function RatingLabel({
	displayRating,
	ratingCount,
}: {
	displayRating: string;
	ratingCount: number;
}): ReactNode {
	return (
		<>
			<span className="text-body-lg-emphasis text-gray-700">
				{displayRating}
			</span>
			{ratingCount > 0 && (
				<span className="text-body-lg text-gray-500">({ratingCount}+)</span>
			)}
		</>
	);
}

function resolveLayoutClass(horizontal: boolean, isDesktop: boolean): string {
	if (horizontal) return "flex items-center gap-1.5 py-1";
	if (isDesktop) return "flex flex-col gap-2 items-start";
	return "";
}

export function ProductDetailsCTAUserRating({
	rating,
	ratingCount,
	horizontal = false,
	ratingBreakdown,
	isBreakdownOpen,
	onBreakdownOpenChange,
	isDesktop,
}: ProductDetailsCTAUserRatingProps) {
	const { t } = useTranslation();
	const ratingClamped = parseRating(rating);
	const displayRating = ratingClamped.toFixed(1);
	const stars = buildStars(ratingClamped);
	const hasBreakdown =
		ratingBreakdown !== undefined && ratingBreakdown.length > 0;

	if (String(rating) === "0") {
		return (
			<div
				className={`flex items-center ${horizontal ? "py-1" : "flex-col gap-2 items-start"}`}
			>
				<div className="text-body-sm text-gray-500">{t("noRatingYet")}</div>
			</div>
		);
	}

	const chevron = hasBreakdown ? (
		<span
			className={`inline-flex transition-transform duration-200 flex-shrink-0 text-gray-700 ${isBreakdownOpen ? "rotate-180" : ""}`}
		>
			<ChevronDownIcon size={5} />
		</span>
	) : null;

	const ratingLabel = (
		<RatingLabel displayRating={displayRating} ratingCount={ratingCount} />
	);

	const horizontalContent = (
		<>
			<div className="flex items-center gap-0.5 text-gray-700">{stars}</div>
			<div className="flex items-center gap-1 whitespace-nowrap">
				{ratingLabel}
			</div>
			{chevron}
		</>
	);

	const verticalContent = isDesktop ? (
		<>
			<div className="flex items-center gap-1">{ratingLabel}</div>
			<div className="flex items-center gap-1">
				<div className="flex gap-0.5 text-gray-700">{stars}</div>
				{chevron}
			</div>
		</>
	) : (
		<div className="flex items-center gap-4">
			<div className="flex flex-col gap-2 items-start">
				<div className="flex items-center gap-1">{ratingLabel}</div>
				<div className="flex gap-0.5 text-gray-700">{stars}</div>
			</div>
			{chevron}
		</div>
	);

	const ratingContent = horizontal ? horizontalContent : verticalContent;

	if (!hasBreakdown) {
		const wrapperClass = resolveLayoutClass(horizontal, isDesktop);
		if (wrapperClass) {
			return <div className={wrapperClass}>{ratingContent}</div>;
		}
		return verticalContent;
	}

	const triggerButton = (
		<button
			type="button"
			className={`cursor-pointer bg-transparent border-0 p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ansay-main focus-visible:ring-offset-2 rounded-md ${resolveLayoutClass(horizontal, isDesktop)}`}
			data-testid="user-rating-breakdown-trigger"
			aria-label={t("ratingBreakdownTitle")}
			aria-expanded={isBreakdownOpen}
			aria-haspopup="dialog"
		>
			{ratingContent}
		</button>
	);

	if (isDesktop) {
		return <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>;
	}

	return (
		<RatingBreakdownDrawer
			averageRating={ratingClamped}
			totalCount={ratingCount}
			breakdown={ratingBreakdown}
			isOpen={isBreakdownOpen}
			onOpenChange={onBreakdownOpenChange}
		>
			{triggerButton}
		</RatingBreakdownDrawer>
	);
}
